import { computed, inject, Injectable, signal } from '@angular/core'
import { Router } from '@angular/router'
import { firstValueFrom, lastValueFrom, shareReplay, Subject, takeLast } from 'rxjs'
import { HttpClient } from '@angular/common/http'
import { AppAuthService, AuthUserProfile, COOKIE_AUTH_CONFIG } from './auth.contract'
import { APP_CONFIG } from 'environments/environment.model'

export interface UserInfoEndpoint {
  email: string
  family_name: string
  given_name: string
  id: string
  enabled: boolean
  root: boolean
  domains: string[]
  org_code?: string | null
  are?: string | null
}

export interface Permission {
  action: string
  project_id: string
  resource: string
  result: string
}

@Injectable()
export class AuthCookieService implements AppAuthService {
  router = inject(Router)
  http = inject(HttpClient)
  cookieAuthConfig = inject(COOKIE_AUTH_CONFIG)

  readonly doneSubject = new Subject<void>()
  readonly done = this.doneSubject.pipe(shareReplay(1))
  readonly #userInfo = signal<UserInfoEndpoint | undefined>(undefined)
  readonly expirationDateTime = signal<Date | undefined>(undefined)

  readonly familyUserName = computed(() => this.#userInfo()?.family_name ?? '')

  readonly givenUserName = computed(() => this.#userInfo()?.given_name ?? '')

  readonly userName = computed(() => {
    const userInfo = this.#userInfo()
    if (userInfo == null) {
      return ''
    }
    return `${userInfo.given_name} ${userInfo.family_name}`.trim()
  })

  readonly userEmail = computed(() => this.#userInfo()?.email ?? '')

  readonly isAuthenticated = computed(() => this.#userInfo() != null)

  readonly initials = computed(() => {
    const given = this.givenUserName()
    const family = this.familyUserName()
    if (given.length > 0 && family.length > 0) {
      return given[0] + family[0]
    }
    return ''
  })

  readonly userProfile = computed<AuthUserProfile | undefined>(() => {
    const userInfo = this.#userInfo()
    if (!userInfo) {
      return undefined
    }

    return {
      displayName: `${userInfo.given_name} ${userInfo.family_name}`.trim(),
      email: userInfo.email,
      initials: this.initials(),
    }
  })

  readonly #apiBaseUrl = inject(APP_CONFIG).url.replace(/\/$/, '')

  readonly #resolveEndpointUrl = (path: string) => `${this.#apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`

  constructor() {
    // This is a hack, because done needs to be subscribed to work
    this.done.subscribe()

    this.http
      .get<UserInfoEndpoint>(this.#resolveEndpointUrl(this.cookieAuthConfig.meUrl))
      .pipe(takeLast(1))
      .subscribe({
        next: (value) => {
          this.#userInfo.set(value)
          this.doneSubject.next()
        },
        error: () => {
          void lastValueFrom(
            this.http.get<UserInfoEndpoint>(this.#resolveEndpointUrl(this.cookieAuthConfig.logoutUrl)),
          ).catch(() => undefined)
          this.doneSubject.next()
        },
      })
  }

  login() {
    window.location.href = this.#resolveEndpointUrl(this.cookieAuthConfig.loginUrl)
  }

  async isLoggedIn(): Promise<boolean> {
    await firstValueFrom(this.done)
    return this.isAuthenticated()
  }

  async isLoggedInAsync(): Promise<boolean> {
    return this.isLoggedIn()
  }

  logout() {
    this.#userInfo.set(undefined)
    void lastValueFrom(
      this.http.get<UserInfoEndpoint>(this.#resolveEndpointUrl(this.cookieAuthConfig.logoutUrl)),
    ).catch(() => undefined)
    this.router.navigate([''])
  }

  getAccessToken(): string | null {
    return null
  }

  redirectToHome() {
    this.router.navigate([''])
  }
}
